import { useEffect } from 'react';
import { RouteComponentProps } from '@reach/router';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { usePolicyStreamGoogleAnalytics } from '../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../hooks/usePolicyStreamGoogleAnalytics/types';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PolicyStreamPageContainer } from './components/PageContainer';

interface PolicyStreamTosProps extends RouteComponentProps {}
export const PolicyStreamTos = (_props: PolicyStreamTosProps) => {
  const myAnalytics = usePolicyStreamGoogleAnalytics();

  // Send Google Analytics event on component load
  useEffect(() => {
    myAnalytics?.triggerEvent(
      GAEventNames.Tos,
      EventActions.PageLoad,
      'PolicyStream Terms of Service Page',
    );
  }, [myAnalytics]);

  return (
    <PolicyStreamPageContainer>
      <BackButton />
      <Typography variant='h4' gutterBottom>
        Terms of Use
      </Typography>

      <Typography variant='h6' fontWeight='bold' gutterBottom>
        Terms and Conditions of Use
      </Typography>
      <Typography variant='body2' gutterBottom>
        <em>Last updated: July, 2025</em>
      </Typography>

      <Typography paragraph>
        Welcome. By accessing or using this website (“Site”), which is operated
        by a licensed insurance advisor, you agree to the following Terms and
        Conditions of Use (“Terms”) without limitation or qualification. If you
        do not agree to these Terms, you must not use this Site.
      </Typography>

      <Typography paragraph>
        These Terms may be updated from time to time. Continued use of the Site
        after changes are posted indicates your acceptance of the revised Terms.
      </Typography>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          1. Use of Personal Information
        </Typography>
        <Typography paragraph>
          Your personal information may be collected through this Site to
          generate insurance quotes, conduct a needs analysis, assist with
          insurance applications, and contact you about insurance-related
          services. This information may be shared with licensed insurance
          advisors and insurers for these purposes.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          Consent to Contact
        </Typography>
        <Typography paragraph>
          By submitting your information on this Site, you expressly consent to
          be contacted by phone, email, SMS, or other means by a licensed
          advisor or their representatives. This includes marketing and
          transactional communications, even if your number is on a Do Not Call
          list. You may withdraw consent at any time.
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          2. Services Provided
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          a. Needs Analysis Tool
        </Typography>
        <Typography paragraph>
          This Site may include a tool to help estimate your insurance needs.
          This tool is based on general assumptions and does not constitute
          professional advice.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          b. Quote Comparisons
        </Typography>
        <Typography paragraph>
          Quotes are generated using third-party tools and insurer data. These
          quotes are estimates only and may change after underwriting by the
          insurer.
        </Typography>

        <Typography variant='subtitle1' fontWeight='bold' gutterBottom>
          c. Online Applications
        </Typography>
        <Typography paragraph>
          Where available, this Site allows you to begin or submit a life
          insurance application. By doing so, you consent to share your
          information with the selected insurance provider and to be contacted
          by a licensed advisor.
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          3. Disclaimer of Liability
        </Typography>
        <Typography paragraph>
          While reasonable efforts are made to ensure accuracy, this Site and
          its operators are not liable for any errors, omissions, or reliance on
          the information provided. Quotes are estimates and subject to change
          based on insurer review. You may cancel or decline any insurance offer
          at any time.
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          4. Confidentiality and Security
        </Typography>
        <Typography paragraph>
          We use reasonable safeguards to protect your data. However, the
          internet is not completely secure, and we cannot guarantee the
          confidentiality of information submitted online.
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          5. Privacy
        </Typography>
        <Typography paragraph>
          Your personal information will only be collected, used, and shared as
          needed to provide insurance-related services and communications. No
          personal information is sold or shared for unrelated third-party
          marketing purposes.
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          6. Jurisdiction
        </Typography>
        <Typography paragraph>
          This Site is intended for residents of jurisdictions where the advisor
          is duly licensed. If you reside outside those areas, services may not
          be available to you.
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          7. Prohibited Use
        </Typography>
        <Typography paragraph>You agree not to:</Typography>
        <List dense>
          <ListItem>
            <ListItemText primary='Use this Site for any unlawful purpose;' />
          </ListItem>
          <ListItem>
            <ListItemText primary='Submit false or misleading information;' />
          </ListItem>
          <ListItem>
            <ListItemText primary='Interfere with or disrupt the operation of the Site;' />
          </ListItem>
          <ListItem>
            <ListItemText primary='Attempt to access unauthorized data or systems.' />
          </ListItem>
        </List>
      </Box>

      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' gutterBottom>
          8. Intellectual Property
        </Typography>
        <Typography paragraph>
          All content on this Site is the property of its respective owner. You
          may not copy, reproduce, or modify any content without prior written
          permission.
        </Typography>
      </Box>
      <BackButton />
    </PolicyStreamPageContainer>
  );
};

const BackButton = () => {
  return (
    <Button
      variant='outlined'
      startIcon={<ArrowBackIcon />}
      onClick={() => {
        window.history.back();
      }}
    >
      Back
    </Button>
  );
};
